import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { 
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Receipt,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  PieChart,
  BarChart3,
  Calendar,
  Download,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';

export const FinancialDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [financialData, setFinancialData] = useState({
    summary: {
      totalRevenue: 0,
      totalExpenses: 0,
      netIncome: 0,
      outstandingAR: 0,
      trustBalance: 0
    },
    revenueByMonth: [],
    expensesByCategory: [],
    recentTransactions: [],
    invoiceAging: { current: 0, thirtyDays: 0, sixtyDays: 0, ninetyPlus: 0 }
  });

  useEffect(() => {
    fetchFinancialData();
  }, [period]);

  const fetchFinancialData = async () => {
    try {
      const response = await api.get(`/financials/dashboard?period=${period}`);
      setFinancialData(response.data);
    } catch (error) {
      console.error('Failed to fetch financial data:', error);
      // Set demo data
      setFinancialData({
        summary: {
          totalRevenue: 125750.00,
          totalExpenses: 45320.00,
          netIncome: 80430.00,
          outstandingAR: 32450.00,
          trustBalance: 156780.00,
          revenueChange: 12.5,
          expenseChange: -3.2
        },
        revenueByMonth: [
          { month: 'Oct', amount: 28500 },
          { month: 'Nov', amount: 32100 },
          { month: 'Dec', amount: 35200 },
          { month: 'Jan', amount: 29950 }
        ],
        expensesByCategory: [
          { category: 'Salaries', amount: 28000, percentage: 62 },
          { category: 'Office', amount: 5500, percentage: 12 },
          { category: 'Marketing', amount: 4200, percentage: 9 },
          { category: 'Software', amount: 3800, percentage: 8 },
          { category: 'Other', amount: 3820, percentage: 9 }
        ],
        recentTransactions: [
          { id: 1, type: 'income', description: 'Invoice #1024 - Smith v. Jones', amount: 5500, date: '2026-01-14', client: 'Robert Smith' },
          { id: 2, type: 'expense', description: 'Office Supplies', amount: -320, date: '2026-01-13', category: 'Office' },
          { id: 3, type: 'income', description: 'Retainer - Johnson LLC', amount: 10000, date: '2026-01-12', client: 'Johnson LLC' },
          { id: 4, type: 'expense', description: 'Legal Research Service', amount: -450, date: '2026-01-11', category: 'Software' },
          { id: 5, type: 'income', description: 'Invoice #1023 - Estate Planning', amount: 3200, date: '2026-01-10', client: 'Mary Williams' }
        ],
        invoiceAging: {
          current: 18500,
          thirtyDays: 8200,
          sixtyDays: 4250,
          ninetyPlus: 1500
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const maxRevenueMonth = Math.max(...financialData.revenueByMonth.map(m => m.amount));

  if (loading) {
    return <PageLoader text="Loading financial dashboard..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="financial-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Financial Dashboard</h1>
          <p className="text-slate-500 mt-1">Overview of your firm's financial health</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card data-testid="total-revenue-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="p-2 rounded-lg bg-green-100">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              {financialData.summary.revenueChange !== undefined && (
                <Badge variant={financialData.summary.revenueChange >= 0 ? "default" : "destructive"} className="gap-1">
                  {financialData.summary.revenueChange >= 0 ? (
                    <ArrowUpRight className="h-3 w-3" />
                  ) : (
                    <ArrowDownRight className="h-3 w-3" />
                  )}
                  {Math.abs(financialData.summary.revenueChange)}%
                </Badge>
              )}
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-3">
              {formatCurrency(financialData.summary.totalRevenue)}
            </p>
            <p className="text-sm text-slate-500">Total Revenue</p>
          </CardContent>
        </Card>

        <Card data-testid="total-expenses-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="p-2 rounded-lg bg-red-100">
                <CreditCard className="h-5 w-5 text-red-600" />
              </div>
              {financialData.summary.expenseChange !== undefined && (
                <Badge variant={financialData.summary.expenseChange <= 0 ? "default" : "destructive"} className="gap-1">
                  {financialData.summary.expenseChange <= 0 ? (
                    <ArrowDownRight className="h-3 w-3" />
                  ) : (
                    <ArrowUpRight className="h-3 w-3" />
                  )}
                  {Math.abs(financialData.summary.expenseChange)}%
                </Badge>
              )}
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-3">
              {formatCurrency(financialData.summary.totalExpenses)}
            </p>
            <p className="text-sm text-slate-500">Total Expenses</p>
          </CardContent>
        </Card>

        <Card data-testid="net-income-card">
          <CardContent className="p-5">
            <div className="p-2 rounded-lg bg-blue-100 w-fit">
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-3">
              {formatCurrency(financialData.summary.netIncome)}
            </p>
            <p className="text-sm text-slate-500">Net Income</p>
          </CardContent>
        </Card>

        <Card data-testid="outstanding-ar-card">
          <CardContent className="p-5">
            <div className="p-2 rounded-lg bg-amber-100 w-fit">
              <Receipt className="h-5 w-5 text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-3">
              {formatCurrency(financialData.summary.outstandingAR)}
            </p>
            <p className="text-sm text-slate-500">Outstanding A/R</p>
          </CardContent>
        </Card>

        <Card data-testid="trust-balance-card">
          <CardContent className="p-5">
            <div className="p-2 rounded-lg bg-purple-100 w-fit">
              <DollarSign className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-600 mt-3">
              {formatCurrency(financialData.summary.trustBalance)}
            </p>
            <p className="text-sm text-slate-500">Trust Balance</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <Card className="lg:col-span-2" data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-500" />
              Revenue Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between h-48 gap-4">
              {financialData.revenueByMonth.map((month, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                  <div 
                    className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded-t-lg transition-all duration-500"
                    style={{ height: `${(month.amount / maxRevenueMonth) * 160}px` }}
                  />
                  <span className="text-xs text-slate-500">{month.month}</span>
                  <span className="text-xs font-medium">{formatCurrency(month.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Expenses Breakdown */}
        <Card data-testid="expenses-breakdown">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5 text-red-500" />
              Expenses Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {financialData.expensesByCategory.map((expense, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{expense.category}</span>
                  <span className="font-medium">{formatCurrency(expense.amount)}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={cn(
                      "h-full rounded-full transition-all duration-500",
                      idx === 0 && "bg-red-500",
                      idx === 1 && "bg-orange-500",
                      idx === 2 && "bg-amber-500",
                      idx === 3 && "bg-blue-500",
                      idx === 4 && "bg-slate-400"
                    )}
                    style={{ width: `${expense.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Invoice Aging */}
        <Card data-testid="invoice-aging">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Invoice Aging
            </CardTitle>
            <CardDescription>Accounts receivable by age</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-center">
                <p className="text-lg font-bold text-green-700">
                  {formatCurrency(financialData.invoiceAging.current)}
                </p>
                <p className="text-xs text-green-600 mt-1">Current</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-center">
                <p className="text-lg font-bold text-amber-700">
                  {formatCurrency(financialData.invoiceAging.thirtyDays)}
                </p>
                <p className="text-xs text-amber-600 mt-1">30 Days</p>
              </div>
              <div className="p-4 rounded-lg bg-orange-50 border border-orange-200 text-center">
                <p className="text-lg font-bold text-orange-700">
                  {formatCurrency(financialData.invoiceAging.sixtyDays)}
                </p>
                <p className="text-xs text-orange-600 mt-1">60 Days</p>
              </div>
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-center">
                <p className="text-lg font-bold text-red-700">
                  {formatCurrency(financialData.invoiceAging.ninetyPlus)}
                </p>
                <p className="text-xs text-red-600 mt-1">90+ Days</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card data-testid="recent-transactions">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="h-5 w-5 text-blue-500" />
              Recent Transactions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y max-h-[300px] overflow-y-auto">
              {financialData.recentTransactions.map((tx) => (
                <div key={tx.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center",
                      tx.type === 'income' ? "bg-green-100" : "bg-red-100"
                    )}>
                      {tx.type === 'income' ? (
                        <ArrowUpRight className="h-4 w-4 text-green-600" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{tx.description}</p>
                      <p className="text-xs text-slate-500">{tx.client || tx.category}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={cn(
                      "font-medium",
                      tx.type === 'income' ? "text-green-600" : "text-red-600"
                    )}>
                      {tx.type === 'income' ? '+' : ''}{formatCurrency(tx.amount)}
                    </p>
                    <p className="text-xs text-slate-400">{formatDate(tx.date)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FinancialDashboard;
